import { useEffect, useMemo, useRef, useState } from "react";
import { geoMercator, geoPath } from "d3-geo";
import { feature } from "topojson-client";
import { getCollegesGeo, ASSET_BASE } from "../lib/api";
import topo from "../data/india-topo.json";

const W = 520;
const H = 600;

export const IndiaCoverageMap = ({ focusCode = null }) => {
  const [data, setData] = useState(null);
  const [hover, setHover] = useState(null);
  const [tour, setTour] = useState(null); // {c, px, py} during intro wave
  const [mounted, setMounted] = useState(false);
  const wrapRef = useRef(null);
  const tourTimers = useRef([]);

  useEffect(() => {
    getCollegesGeo()
      .then((res) => setData(res.data))
      .catch(() => setData({ points: [], total_colleges: 0, states: 0 }));
    const t = setTimeout(() => setMounted(true), 100);
    return () => clearTimeout(t);
  }, []);

  const { statePaths, project } = useMemo(() => {
    const india = feature(topo, topo.objects.india);
    const features = india.features || [india];
    const projection = geoMercator().fitSize([W, H], india);
    const pathGen = geoPath(projection);
    const palette = [
      "#DBEAFE", "#FCE7F3", "#DCFCE7", "#FEF3C7", "#EDE9FE",
      "#CFFAFE", "#FFE4E6", "#FEF9C3", "#E0E7FF", "#FAE8FF",
      "#D1FAE5", "#FFEDD5",
    ];
    const statePaths = features.map((f, i) => ({
      d: pathGen(f),
      fill: palette[i % palette.length],
      key: f.properties?.name || i,
    }));
    return { statePaths, project: projection };
  }, []);

  const points = useMemo(() => {
    if (!data?.points) return [];
    return data.points
      .map((p) => {
        const xy = project([p.lng, p.lat]);
        return xy ? { ...p, x: xy[0], y: xy[1] } : null;
      })
      .filter(Boolean);
  }, [data, project]);

  // viewBox(x,y) -> container px (preserveAspectRatio xMidYMid meet)
  const toPx = (x, y) => {
    const el = wrapRef.current;
    const cw = el?.clientWidth || W;
    const ch = el?.clientHeight || H;
    const scale = Math.min(cw / W, ch / H);
    const offX = (cw - W * scale) / 2;
    const offY = (ch - H * scale) / 2;
    return [offX + x * scale, offY + y * scale];
  };

  // Intro wave: one INI per city, north -> south, tooltip one-by-one (single cycle)
  useEffect(() => {
    if (!points.length) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;

    const seen = new Set();
    const inis = points
      .filter((p) => p.ni === 1)
      .sort((a, b) => b.lat - a.lat)
      .filter((p) => {
        const k = (p.ci || "") + "|" + Math.round(p.lat * 5);
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      });
    if (!inis.length) return;

    const step = Math.max(380, Math.min(650, Math.round(11000 / inis.length)));
    let cancelled = false;
    const start = setTimeout(() => {
      inis.forEach((p, i) => {
        const t = setTimeout(() => {
          if (cancelled) return;
          const [px, py] = toPx(p.x, p.y);
          setTour({ ...p, px, py });
          if (i === inis.length - 1) {
            const end = setTimeout(() => !cancelled && setTour(null), step);
            tourTimers.current.push(end);
          }
        }, i * step);
        tourTimers.current.push(t);
      });
    }, 900);
    tourTimers.current.push(start);

    return () => {
      cancelled = true;
      tourTimers.current.forEach(clearTimeout);
      tourTimers.current = [];
    };
  }, [points]); // eslint-disable-line react-hooks/exhaustive-deps

  const cancelTour = () => {
    if (tourTimers.current.length) {
      tourTimers.current.forEach(clearTimeout);
      tourTimers.current = [];
      setTour(null);
    }
  };

  // External focus (e.g. homepage banner carousel) — highlight that college + tooltip
  useEffect(() => {
    if (!focusCode || !points.length) return;
    if (tourTimers.current.length) {
      tourTimers.current.forEach(clearTimeout);
      tourTimers.current = [];
    }
    const p = points.find((pt) => pt.c === focusCode);
    if (!p) return;
    const [px, py] = toPx(p.x, p.y);
    setTour({ ...p, px, py });
  }, [focusCode, points]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMove = (e) => {
    if (!wrapRef.current) return;
    const r = wrapRef.current.getBoundingClientRect();
    setHover((h) => (h ? { ...h, px: e.clientX - r.left, py: e.clientY - r.top } : h));
  };

  const focused = hover || tour;
  const activeCode = focused?.c;

  return (
    <div
      ref={wrapRef}
      onMouseMove={handleMove}
      className="relative w-full h-full select-none"
      data-testid="india-coverage-map"
    >
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-full overflow-visible">
        <defs>
          <radialGradient id="dotGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#6043EF" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#6043EF" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="iniGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#FBBF24" stopOpacity="1" />
            <stop offset="55%" stopColor="#F59E0B" stopOpacity="0.55" />
            <stop offset="100%" stopColor="#F59E0B" stopOpacity="0" />
          </radialGradient>
          <filter id="mapShadow" x="-25%" y="-25%" width="150%" height="150%">
            <feDropShadow dx="0" dy="9" stdDeviation="11" floodColor="#4A28C2" floodOpacity="0.45" />
            <feDropShadow dx="0" dy="3" stdDeviation="3" floodColor="#000000" floodOpacity="0.25" />
          </filter>
        </defs>

        <g filter="url(#mapShadow)">
          {statePaths.map((s) => (
            <path
              key={s.key}
              d={s.d}
              fill={s.fill}
              stroke="#0A0A0A"
              strokeOpacity="0.35"
              strokeWidth="0.6"
              strokeLinejoin="round"
            />
          ))}
        </g>

        {points.map((p, i) => {
          const isNI = p.ni === 1;
          const active = activeCode === p.c;
          return (
            <g
              key={`${p.c}-${i}`}
              transform={`translate(${p.x},${p.y})`}
              style={{
                opacity: mounted ? 1 : 0,
                transition: "opacity 0.5s ease",
                transitionDelay: `${Math.min(i * 1.2, 900)}ms`,
                cursor: "pointer",
              }}
              onMouseEnter={(e) => {
                cancelTour();
                const r = wrapRef.current.getBoundingClientRect();
                setHover({ ...p, px: e.clientX - r.left, py: e.clientY - r.top });
              }}
              onMouseLeave={() => setHover(null)}
              data-testid={`map-dot-${p.c}`}
            >
              <circle r="6" fill="transparent" />
              {isNI && (
                <>
                  {/* bright twinkling halo */}
                  <circle r="6" fill="url(#iniGlow)">
                    <animate attributeName="r" values="4;11;4" dur="1.6s" repeatCount="indefinite" />
                    <animate attributeName="opacity" values="1;0.35;1" dur="1.6s" repeatCount="indefinite" />
                  </circle>
                  {/* expanding sonar ring */}
                  <circle r="3" fill="none" stroke="#FBBF24" strokeWidth="0.8">
                    <animate attributeName="r" values="2;9" dur="1.6s" repeatCount="indefinite" />
                    <animate attributeName="opacity" values="0.9;0" dur="1.6s" repeatCount="indefinite" />
                  </circle>
                </>
              )}
              {active && !isNI && <circle r="6" fill="url(#dotGlow)" />}
              {active && (
                <circle r="9" fill="none" stroke={isNI ? "#FBBF24" : "#6043EF"} strokeWidth="1">
                  <animate attributeName="r" values="6;12" dur="0.9s" repeatCount="1" />
                  <animate attributeName="opacity" values="0.9;0" dur="0.9s" repeatCount="1" />
                </circle>
              )}
              <circle
                r={active ? 3.4 : isNI ? 2.6 : 1.7}
                fill={isNI ? "#F59E0B" : "#6043EF"}
                fillOpacity={active ? 1 : isNI ? 1 : 0.8}
                stroke="#fff"
                strokeWidth={active || isNI ? 0.8 : 0}
              />
            </g>
          );
        })}
      </svg>

      {focused && (
        <div
          className="absolute z-20 pointer-events-none"
          style={{
            left: Math.min(focused.px + 14, (wrapRef.current?.clientWidth || W) - 230),
            top: Math.max(focused.py - 10, 4),
            transition: tour ? "left 0.35s ease, top 0.35s ease" : "none",
          }}
          data-testid="map-tooltip"
        >
          <div className="bg-[#09090B] text-white px-3 py-2 shadow-xl max-w-[220px] border border-white/10">
            <div className="flex items-center gap-2">
              {focused.lg === 1 && (
                <img
                  src={`${ASSET_BASE}/colleges/${focused.c}/logo`}
                  alt=""
                  className="w-6 h-6 object-contain bg-white rounded-sm shrink-0"
                  onError={(e) => (e.target.style.display = "none")}
                />
              )}
              <span className="text-[11px] font-bold leading-tight font-heading">
                {focused.n}
              </span>
            </div>
            <div className="mt-1 flex items-center gap-1.5 flex-wrap">
              <span className="text-[10px] font-['IBM_Plex_Mono'] text-zinc-400 uppercase tracking-wider">
                {focused.ci}{focused.ci && focused.s ? ", " : ""}{focused.s}
              </span>
              {focused.ni === 1 && (
                <span className="text-[8px] font-bold uppercase tracking-wider bg-[#F59E0B] text-black px-1 py-0.5">
                  National Importance
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
